import React from "react";
import useOnlineStatus from "../../hooks/useOnlineStatus";

// ── OfflineBanner (prompt #3) ──────────────────────────────────────────────
// Drop into any interview/candidate page. Shows a sticky amber banner
// while the browser reports navigator.onLine === false, and a brief
// "Reconnected — restoring…" toast-style banner for ~3s after the user
// comes back online (per the spec's "pause recording → resume → upload
// pending chunks" UX).
//
// Mount globally if you want this everywhere, or scope it to the
// interview flow only.

export default function OfflineBanner() {
  const { online, wasOffline } = useOnlineStatus();
  const [showRestored, setShowRestored] = React.useState(false);

  React.useEffect(() => {
    if (online && wasOffline) {
      setShowRestored(true);
      const t = setTimeout(() => setShowRestored(false), 3000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [online, wasOffline]);

  if (online && !showRestored) return null;

  if (!online) {
    return (
      <div
        role="alert"
        aria-live="assertive"
        className="fixed top-0 left-0 right-0 z-toast bg-amber-50 border-b border-amber-200 px-4 py-2.5 text-center shadow-sm"
      >
        <div className="inline-flex items-center gap-2 text-xs font-medium text-amber-900">
          <span
            className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"
            aria-hidden="true"
          />
          You're offline — your progress is saved. We'll reconnect automatically.
        </div>
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed top-0 left-0 right-0 z-toast bg-emerald-50 border-b border-emerald-200 px-4 py-2.5 text-center shadow-sm"
    >
      <div className="inline-flex items-center gap-2 text-xs font-medium text-emerald-900">
        <span aria-hidden="true">✓</span>
        Back online. Restoring…
      </div>
    </div>
  );
}
